import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  GraduationCap,
  Plus, 
  Trash2, 
  MapPin,
  Eye,
  Loader2,
  Calendar,
  User,
  Users,
  FileText,
  Search,
  Building,
  Send,
  Upload,
  ZoomIn,
  CheckCircle2,
  XCircle,
  Clock,
  FileImage,
  File,
  ChevronDown,
  ChevronUp,
  Edit3,
  Shield,
  AlertCircle,
  Award,
  ClipboardList,
  Lock,
  Unlock,
  Target,
  BookOpen,
  ListChecks
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category options
const CATEGORY_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'operations', label: 'Operations' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'technical', label: 'Technical' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'quality', label: 'Quality' },
  { value: 'other', label: 'Other' },
];

// Status options
const STATUS_OPTIONS = [
  { value: 'qualified', label: 'Qualified', color: 'bg-green-500', icon: CheckCircle2 },
  { value: 'provisional', label: 'Provisional', color: 'bg-yellow-500', icon: Clock },
  { value: 'expired', label: 'Expired', color: 'bg-red-500', icon: AlertCircle },
  { value: 'revoked', label: 'Revoked', color: 'bg-gray-500', icon: XCircle },
  { value: 'pending', label: 'Pending', color: 'bg-blue-500', icon: Clock },
];

// Assessment types
const ASSESSMENT_TYPES = [
  { value: 'quiz', label: 'Quiz' },
  { value: 'practical', label: 'Practical' },
  { value: 'observation', label: 'Observation' },
  { value: 'sign_off', label: 'Sign-Off' },
  { value: 'certification', label: 'Certification' },
];

// Assessment results
const ASSESSMENT_RESULTS = [
  { value: 'pass', label: 'Pass', color: 'text-green-500' },
  { value: 'fail', label: 'Fail', color: 'text-red-500' },
  { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
  { value: 'incomplete', label: 'Incomplete', color: 'text-gray-500' },
];

// Revalidation frequencies
const REVALIDATION_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'biennial', label: 'Biennial (2 years)' },
  { value: 'triennial', label: 'Triennial (3 years)' },
  { value: 'as_needed', label: 'As Needed' },
];

// Document types
const DOC_TYPES = [
  { value: 'training_record', label: 'Training Record' },
  { value: 'quiz_result', label: 'Quiz Result' },
  { value: 'sign_off', label: 'Sign-Off Record' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'observation_form', label: 'Observation Form' },
];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && <div className="p-3 pt-0 border-t">{children}</div>}
    </div>
  );
};

export function CompetencyPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigate
}) {
  const { token, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docType, setDocType] = useState('certificate');
  const fileInputRef = useRef(null);

  // Form state
  const initialFormState = {
    name: '',
    description: '',
    category: 'operations',
    role_function: '',
    // Applicability
    building_id: '',
    area_room: '',
    applicable_assets: '',
    applicable_instrumentation: '',
    applicable_workflows: '',
    // Requirements
    required_knowledge: '',
    required_skills: '',
    required_assessments: '',
    passing_criteria: '',
    // Status
    status: 'pending',
    date_achieved: '',
    effective_date: '',
    expiration_date: '',
    revalidation_frequency: '',
    // Assessment
    assessment_type: '',
    assessment_result: '',
    assessment_date: '',
    assessed_by: '',
    assessment_notes: '',
    // Permissions
    permitted_activities: '',
    restricted_activities: '',
    supervision_required: false,
    // Ownership
    competency_owner: '',
    approving_authority: '',
    responsible_team: '',
    // Relationships
    related_assets: '',
    related_risks: '',
    related_compliance: '',
    related_workflows: '',
    // Notes
    notes: '',
    // Position
    capturePosition: false,
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // Fetch competencies
  const fetchItems = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/competencies?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching competencies:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch buildings
  const fetchBuildings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setBuildings(await response.json());
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      fetchBuildings();
    }
  }, [isOpen, fetchItems, fetchBuildings]);

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.competency_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.role_function?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    setSubmitting(true);
    try {
      const building = buildings.find(b => b.id === formData.building_id);
      
      const submitData = {
        name: formData.name,
        description: formData.description || null,
        category: formData.category,
        role_function: formData.role_function || null,
        building_id: formData.building_id || null,
        building_name: building?.name || null,
        area_room: formData.area_room || null,
        applicable_assets: formData.applicable_assets ? formData.applicable_assets.split(',').map(s => s.trim()) : [],
        applicable_instrumentation: formData.applicable_instrumentation ? formData.applicable_instrumentation.split(',').map(s => s.trim()) : [],
        applicable_workflows: formData.applicable_workflows ? formData.applicable_workflows.split(',').map(s => s.trim()) : [],
        requirements: {
          required_knowledge: formData.required_knowledge || null,
          required_skills: formData.required_skills ? formData.required_skills.split(',').map(s => s.trim()) : [],
          required_assessments: formData.required_assessments ? formData.required_assessments.split(',').map(s => s.trim()) : [],
          passing_criteria: formData.passing_criteria || null,
        },
        status: formData.status,
        date_achieved: formData.date_achieved || null,
        effective_date: formData.effective_date || null,
        expiration_date: formData.expiration_date || null,
        revalidation_frequency: formData.revalidation_frequency || null,
        assessment: {
          assessment_type: formData.assessment_type || null,
          assessment_result: formData.assessment_result || null,
          assessment_date: formData.assessment_date || null,
          assessed_by: formData.assessed_by || null,
          assessment_notes: formData.assessment_notes || null,
        },
        permissions: {
          permitted_activities: formData.permitted_activities ? formData.permitted_activities.split(',').map(s => s.trim()) : [],
          restricted_activities: formData.restricted_activities ? formData.restricted_activities.split(',').map(s => s.trim()) : [],
          supervision_required: formData.supervision_required,
        },
        competency_owner: formData.competency_owner || null,
        approving_authority: formData.approving_authority || null,
        responsible_team: formData.responsible_team || null,
        relationships: {
          related_assets: formData.related_assets ? formData.related_assets.split(',').map(s => s.trim()) : [],
          related_risks: formData.related_risks ? formData.related_risks.split(',').map(s => s.trim()) : [],
          related_compliance: formData.related_compliance ? formData.related_compliance.split(',').map(s => s.trim()) : [],
          related_workflows: formData.related_workflows ? formData.related_workflows.split(',').map(s => s.trim()) : [],
        },
        notes: formData.notes || null,
        tour_id: currentTour.id,
      };

      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) submitData.position = cameraPosition.position;
        if (cameraPosition?.rotation) submitData.rotation = cameraPosition.rotation;
      }

      const url = isEditMode 
        ? `${API_URL}/api/competencies/${selectedItem.id}`
        : `${API_URL}/api/competencies`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        if (isEditMode) {
          const updatedItem = await response.json();
          setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
          setSelectedItem(updatedItem);
        } else {
          await fetchItems();
        }
        handleCloseDialog();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save competency');
      }
    } catch (error) {
      console.error('Error saving competency:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setFormData({
      name: item.name || '',
      description: item.description || '',
      category: item.category || 'operations',
      role_function: item.role_function || '',
      building_id: item.building_id || '',
      area_room: item.area_room || '',
      applicable_assets: item.applicable_assets?.join(', ') || '',
      applicable_instrumentation: item.applicable_instrumentation?.join(', ') || '',
      applicable_workflows: item.applicable_workflows?.join(', ') || '',
      required_knowledge: item.requirements?.required_knowledge || '',
      required_skills: item.requirements?.required_skills?.join(', ') || '',
      required_assessments: item.requirements?.required_assessments?.join(', ') || '',
      passing_criteria: item.requirements?.passing_criteria || '',
      status: item.status || 'pending',
      date_achieved: item.date_achieved || '',
      effective_date: item.effective_date || '',
      expiration_date: item.expiration_date || '',
      revalidation_frequency: item.revalidation_frequency || '',
      assessment_type: item.assessment?.assessment_type || '',
      assessment_result: item.assessment?.assessment_result || '',
      assessment_date: item.assessment?.assessment_date || '',
      assessed_by: item.assessment?.assessed_by || '',
      assessment_notes: item.assessment?.assessment_notes || '',
      permitted_activities: item.permissions?.permitted_activities?.join(', ') || '',
      restricted_activities: item.permissions?.restricted_activities?.join(', ') || '',
      supervision_required: item.permissions?.supervision_required || false,
      competency_owner: item.competency_owner || '',
      approving_authority: item.approving_authority || '',
      responsible_team: item.responsible_team || '',
      related_assets: item.relationships?.related_assets?.join(', ') || '',
      related_risks: item.relationships?.related_risks?.join(', ') || '',
      related_compliance: item.relationships?.related_compliance?.join(', ') || '',
      related_workflows: item.relationships?.related_workflows?.join(', ') || '',
      notes: item.notes || '',
      capturePosition: !!item.sweep_id,
    });
    setIsEditMode(true);
    setIsDetailOpen(false);
    setIsCreateDialogOpen(true);
  };

  // Handle status update (only for non-viewers)
  const handleStatusUpdate = async (itemId, newStatus) => {
    if (!token || isViewer) return;
    try {
      const response = await fetch(`${API_URL}/api/competencies/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updatedItem = await response.json();
        setItems(prev => prev.map(i => i.id === itemId ? updatedItem : i));
        if (selectedItem?.id === itemId) setSelectedItem(updatedItem);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!token || !selectedItem || !newComment.trim() || isViewer) return;
    try {
      const response = await fetch(
        `${API_URL}/api/competencies/${selectedItem.id}/activity?action=comment&details=${encodeURIComponent(newComment.trim())}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedItem = await response.json();
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Handle document upload (only for non-viewers)
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !token || !selectedItem || isViewer) return;
    if (file.size > 15 * 1024 * 1024) { alert('File too large. Max 15MB.'); return; }

    setUploadingDoc(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await fetch(
        `${API_URL}/api/competencies/${selectedItem.id}/documents/upload?doc_type=${docType}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formDataObj }
      );
      if (response.ok) {
        const updatedItem = await response.json();
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle document delete
  const handleDeleteDoc = async (docId) => {
    if (!token || !selectedItem || isViewer || !window.confirm('Delete this document?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/competencies/${selectedItem.id}/documents/${docId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedDocs = selectedItem.documents.filter(d => d.id !== docId);
        const updatedItem = { ...selectedItem, documents: updatedDocs };
        setSelectedItem(updatedItem);
        setItems(prev => prev.map(i => i.id === selectedItem.id ? updatedItem : i));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Handle delete
  const handleDelete = async (itemId) => {
    if (!token || isViewer || !window.confirm('Delete this competency?')) return;
    try {
      const response = await fetch(`${API_URL}/api/competencies/${itemId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setItems(prev => prev.filter(i => i.id !== itemId));
        if (selectedItem?.id === itemId) { setSelectedItem(null); setIsDetailOpen(false); }
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setIsEditMode(false);
    setFormData(initialFormState);
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    if (!option) return null;
    const Icon = option.icon;
    return (
      <Badge className={`${option.color} text-white gap-1`}>
        <Icon className="h-3 w-3" />
        {option.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : null;
  const formatTimestamp = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '';

  // Calculate stats
  const stats = {
    qualified: items.filter(i => i.status === 'qualified').length,
    provisional: items.filter(i => i.status === 'provisional').length,
    expired: items.filter(i => i.status === 'expired').length,
    pending: items.filter(i => i.status === 'pending').length,
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              Competency
            </SheetTitle>
            <SheetDescription>
              Track personnel competencies and certifications
              {isViewer && (
                <span className="block mt-1 text-orange-500 text-xs">
                  View-only mode - Contact admin to update competencies
                </span>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 pb-4 space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-2">
              <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                <p className="text-lg font-bold text-green-600">{stats.qualified}</p>
                <p className="text-[10px] text-muted-foreground">Qualified</p>
              </div>
              <div className="p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-center">
                <p className="text-lg font-bold text-yellow-600">{stats.provisional}</p>
                <p className="text-[10px] text-muted-foreground">Provisional</p>
              </div>
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                <p className="text-lg font-bold text-red-600">{stats.expired}</p>
                <p className="text-[10px] text-muted-foreground">Expired</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-center">
                <p className="text-lg font-bold text-blue-600">{stats.pending}</p>
                <p className="text-[10px] text-muted-foreground">Pending</p>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search competencies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              {!isViewer && (
                <Button onClick={() => { setIsEditMode(false); setFormData(initialFormState); setIsCreateDialogOpen(true); }}>
                  <Plus className="h-4 w-4 mr-1" />New
                </Button>
              )}
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Items List */}
          <ScrollArea className="h-[calc(100vh-340px)] px-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No competencies found</p>
                <p className="text-sm">Add competencies to track qualifications</p>
              </div>
            ) : (
              <div className="space-y-2 pb-4">
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`competency-item-${item.competency_id}`}
                    className="p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => { setSelectedItem(item); setIsDetailOpen(true); }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-muted-foreground">{item.competency_id}</span>
                          {getStatusBadge(item.status)}
                        </div>
                        <p className="font-medium truncate">{item.name}</p>
                        {item.role_function && (
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            <User className="h-3 w-3 inline mr-1" />
                            {item.role_function}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-[10px]">
                            {CATEGORY_OPTIONS.find(c => c.value === item.category)?.label || item.category}
                          </Badge>
                          {item.expiration_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Expires: {formatDate(item.expiration_date)}
                            </span>
                          )}
                          {item.sweep_id && (
                            <Eye className="h-3 w-3 text-primary" title="Has 3D position" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Competency' : 'New Competency'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Update competency details' : 'Define a new competency requirement'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Identity */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Competency Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Confined Space Entry"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Detailed description"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Role / Function</Label>
                  <Input value={formData.role_function} onChange={(e) => setFormData({...formData, role_function: e.target.value})} placeholder="e.g., Operator, Technician" />
                </div>
              </div>
            </div>

            {/* Applicability */}
            <CollapsibleSection title="Applicability" icon={Building} defaultOpen={true}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Building</Label>
                    <Select value={formData.building_id || 'none'} onValueChange={(value) => setFormData({...formData, building_id: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select building" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Area / Room</Label>
                    <Input value={formData.area_room} onChange={(e) => setFormData({...formData, area_room: e.target.value})} placeholder="e.g., Boiler Room" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Applicable Assets (comma-separated)</Label>
                  <Input value={formData.applicable_assets} onChange={(e) => setFormData({...formData, applicable_assets: e.target.value})} placeholder="e.g., Boiler-01, Compressor-02" />
                </div>
                <div className="space-y-2">
                  <Label>Applicable Instrumentation (comma-separated)</Label>
                  <Input value={formData.applicable_instrumentation} onChange={(e) => setFormData({...formData, applicable_instrumentation: e.target.value})} placeholder="e.g., PT-101, FT-205" />
                </div>
                <div className="space-y-2">
                  <Label>Applicable Workflows (comma-separated)</Label>
                  <Input value={formData.applicable_workflows} onChange={(e) => setFormData({...formData, applicable_workflows: e.target.value})} placeholder="e.g., Startup, Shutdown" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Requirements */}
            <CollapsibleSection title="Requirements" icon={BookOpen}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Required Knowledge</Label>
                  <Textarea value={formData.required_knowledge} onChange={(e) => setFormData({...formData, required_knowledge: e.target.value})} placeholder="What knowledge is required?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Required Skills / Tasks (comma-separated)</Label>
                  <Input value={formData.required_skills} onChange={(e) => setFormData({...formData, required_skills: e.target.value})} placeholder="e.g., Gas testing, Permit completion" />
                </div>
                <div className="space-y-2">
                  <Label>Required Assessments (comma-separated)</Label>
                  <Input value={formData.required_assessments} onChange={(e) => setFormData({...formData, required_assessments: e.target.value})} placeholder="e.g., Written test, Practical demo" />
                </div>
                <div className="space-y-2">
                  <Label>Passing Criteria</Label>
                  <Input value={formData.passing_criteria} onChange={(e) => setFormData({...formData, passing_criteria: e.target.value})} placeholder="e.g., 80% on written, Supervisor sign-off" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Status & Timing */}
            <CollapsibleSection title="Status & Timing" icon={Clock}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Revalidation Frequency</Label>
                    <Select value={formData.revalidation_frequency || 'none'} onValueChange={(value) => setFormData({...formData, revalidation_frequency: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {REVALIDATION_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Date Achieved</Label>
                    <Input type="date" value={formData.date_achieved} onChange={(e) => setFormData({...formData, date_achieved: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Effective Date</Label>
                    <Input type="date" value={formData.effective_date} onChange={(e) => setFormData({...formData, effective_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiration Date</Label>
                    <Input type="date" value={formData.expiration_date} onChange={(e) => setFormData({...formData, expiration_date: e.target.value})} />
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Assessment */}
            <CollapsibleSection title="Assessment" icon={ClipboardList}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assessment Type</Label>
                    <Select value={formData.assessment_type || 'none'} onValueChange={(value) => setFormData({...formData, assessment_type: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {ASSESSMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Assessment Result</Label>
                    <Select value={formData.assessment_result || 'none'} onValueChange={(value) => setFormData({...formData, assessment_result: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {ASSESSMENT_RESULTS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Assessment Date</Label>
                    <Input type="date" value={formData.assessment_date} onChange={(e) => setFormData({...formData, assessment_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>Assessed By</Label>
                    <Input value={formData.assessed_by} onChange={(e) => setFormData({...formData, assessed_by: e.target.value})} placeholder="Assessor name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Assessment Notes</Label>
                  <Textarea value={formData.assessment_notes} onChange={(e) => setFormData({...formData, assessment_notes: e.target.value})} placeholder="Notes from assessment" rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            {/* Permissions & Scope */}
            <CollapsibleSection title="Permissions & Scope" icon={Shield}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Permitted Activities (comma-separated)</Label>
                  <Textarea value={formData.permitted_activities} onChange={(e) => setFormData({...formData, permitted_activities: e.target.value})} placeholder="What can this person do?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Restricted Activities (comma-separated)</Label>
                  <Textarea value={formData.restricted_activities} onChange={(e) => setFormData({...formData, restricted_activities: e.target.value})} placeholder="What is NOT allowed?" rows={2} />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="supervision_required"
                    checked={formData.supervision_required}
                    onChange={(e) => setFormData({...formData, supervision_required: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="supervision_required">Supervision Required</Label>
                </div>
              </div>
            </CollapsibleSection>

            {/* Ownership */}
            <CollapsibleSection title="Ownership & Accountability" icon={Users}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Competency Owner</Label>
                    <Input value={formData.competency_owner} onChange={(e) => setFormData({...formData, competency_owner: e.target.value})} placeholder="Name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Approving Authority</Label>
                    <Input value={formData.approving_authority} onChange={(e) => setFormData({...formData, approving_authority: e.target.value})} placeholder="Who signs off?" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Responsible Team</Label>
                  <Input value={formData.responsible_team} onChange={(e) => setFormData({...formData, responsible_team: e.target.value})} placeholder="e.g., Training Dept" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Relationships */}
            <CollapsibleSection title="Relationships" icon={ListChecks}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Related Assets (comma-separated)</Label>
                  <Input value={formData.related_assets} onChange={(e) => setFormData({...formData, related_assets: e.target.value})} placeholder="Asset IDs" />
                </div>
                <div className="space-y-2">
                  <Label>Related Risks (comma-separated)</Label>
                  <Input value={formData.related_risks} onChange={(e) => setFormData({...formData, related_risks: e.target.value})} placeholder="Risk IDs" />
                </div>
                <div className="space-y-2">
                  <Label>Related Compliance Items (comma-separated)</Label>
                  <Input value={formData.related_compliance} onChange={(e) => setFormData({...formData, related_compliance: e.target.value})} placeholder="Compliance IDs" />
                </div>
                <div className="space-y-2">
                  <Label>Related Workflows (comma-separated)</Label>
                  <Input value={formData.related_workflows} onChange={(e) => setFormData({...formData, related_workflows: e.target.value})} placeholder="Workflow IDs" />
                </div>
              </div>
            </CollapsibleSection>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Any other notes" rows={2} />
            </div>

            {/* 3D Position */}
            <div className="p-3 rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Link to 3D Position</Label>
                <Button type="button" variant={formData.capturePosition ? "default" : "outline"} size="sm" onClick={() => setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}>
                  {formData.capturePosition ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              {formData.capturePosition ? (
                currentSweep?.id ? (
                  <p className="text-xs text-green-600">✓ Current camera position will be saved</p>
                ) : (
                  <p className="text-xs text-yellow-600">⚠ Navigate to a location in the 3D viewer first</p>
                )
              ) : (
                isEditMode && selectedItem?.sweep_id && (
                  <p className="text-xs text-orange-600">⚠ Saving will clear the existing position</p>
                )
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditMode ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-mono">{selectedItem.competency_id}</span>
                    {getStatusBadge(selectedItem.status)}
                  </div>
                  {!isViewer && (
                    <Button variant="outline" size="sm" onClick={() => handleEdit(selectedItem)}>
                      <Edit3 className="h-4 w-4 mr-2" />Edit
                    </Button>
                  )}
                </div>
                <DialogTitle>{selectedItem.name}</DialogTitle>
                {selectedItem.role_function && (
                  <p className="text-sm text-muted-foreground">Role: {selectedItem.role_function}</p>
                )}
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="permissions">Permissions</TabsTrigger>
                  <TabsTrigger value="evidence">Evidence ({selectedItem.documents?.length || 0})</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  {!isViewer && (
                    <div className="flex items-center gap-2">
                      <Label>Status:</Label>
                      <Select value={selectedItem.status} onValueChange={(value) => handleStatusUpdate(selectedItem.id, value)}>
                        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {selectedItem.description && <div><Label className="text-muted-foreground">Description</Label><p className="mt-1">{selectedItem.description}</p></div>}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><Label className="text-muted-foreground">Category</Label><p>{CATEGORY_OPTIONS.find(c => c.value === selectedItem.category)?.label}</p></div>
                    {selectedItem.building_name && <div><Label className="text-muted-foreground">Building</Label><p>{selectedItem.building_name}</p></div>}
                    {selectedItem.date_achieved && <div><Label className="text-muted-foreground">Date Achieved</Label><p>{formatDate(selectedItem.date_achieved)}</p></div>}
                    {selectedItem.expiration_date && <div><Label className="text-muted-foreground">Expires</Label><p>{formatDate(selectedItem.expiration_date)}</p></div>}
                    {selectedItem.competency_owner && <div><Label className="text-muted-foreground">Owner</Label><p>{selectedItem.competency_owner}</p></div>}
                    {selectedItem.revalidation_frequency && <div><Label className="text-muted-foreground">Revalidation</Label><p>{REVALIDATION_OPTIONS.find(r => r.value === selectedItem.revalidation_frequency)?.label}</p></div>}
                  </div>

                  {/* Assessment */}
                  {selectedItem.assessment?.assessment_result && (
                    <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                      <Label className="font-semibold">Assessment</Label>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-muted-foreground">Type:</span> {ASSESSMENT_TYPES.find(t => t.value === selectedItem.assessment.assessment_type)?.label}</div>
                        <div><span className="text-muted-foreground">Result:</span> <span className={ASSESSMENT_RESULTS.find(r => r.value === selectedItem.assessment.assessment_result)?.color}>{selectedItem.assessment.assessment_result?.toUpperCase()}</span></div>
                        {selectedItem.assessment.assessed_by && <div><span className="text-muted-foreground">Assessed By:</span> {selectedItem.assessment.assessed_by}</div>}
                        {selectedItem.assessment.assessment_date && <div><span className="text-muted-foreground">Date:</span> {formatDate(selectedItem.assessment.assessment_date)}</div>}
                      </div>
                    </div>
                  )}

                  {selectedItem.sweep_id && (
                    <Button variant="outline" className="w-full" onClick={() => { onNavigate?.(selectedItem.sweep_id, { rotation: selectedItem.rotation, transitionTime: 1500 }); setIsDetailOpen(false); onClose(); }}>
                      <MapPin className="h-4 w-4 mr-2" />Go to Location
                    </Button>
                  )}
                </TabsContent>

                <TabsContent value="permissions" className="mt-4 space-y-4">
                  {selectedItem.permissions ? (
                    <>
                      {selectedItem.permissions.permitted_activities?.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2"><Unlock className="h-4 w-4 text-green-500" />Permitted Activities</Label>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedItem.permissions.permitted_activities.map((a, i) => (
                              <Badge key={i} variant="outline" className="bg-green-500/10 border-green-500/30">{a}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedItem.permissions.restricted_activities?.length > 0 && (
                        <div>
                          <Label className="text-muted-foreground flex items-center gap-2"><Lock className="h-4 w-4 text-red-500" />Restricted Activities</Label>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedItem.permissions.restricted_activities.map((a, i) => (
                              <Badge key={i} variant="outline" className="bg-red-500/10 border-red-500/30">{a}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                        {selectedItem.permissions.supervision_required ? (
                          <><AlertCircle className="h-5 w-5 text-yellow-500" /><span>Supervision Required</span></>
                        ) : (
                          <><CheckCircle2 className="h-5 w-5 text-green-500" /><span>Can work independently</span></>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No permissions defined</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="evidence" className="mt-4">
                  <div className="space-y-4">
                    {!isViewer && (
                      <div className="flex gap-2">
                        <Select value={docType} onValueChange={setDocType}>
                          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <input type="file" ref={fileInputRef} onChange={handleDocUpload} className="hidden" />
                        <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="flex-1">
                          {uploadingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-2" />Upload</>}
                        </Button>
                      </div>
                    )}

                    {selectedItem.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedItem.documents.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              {doc.url?.startsWith('data:image') ? <FileImage className="h-5 w-5 text-blue-500" /> : <File className="h-5 w-5 text-gray-500" />}
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {DOC_TYPES.find(t => t.value === doc.doc_type)?.label} • {doc.uploaded_by_name} • {formatTimestamp(doc.uploaded_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {doc.url?.startsWith('data:image') && <Button variant="ghost" size="icon" onClick={() => setSelectedDoc(doc)}><ZoomIn className="h-4 w-4" /></Button>}
                              {!isViewer && <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No evidence uploaded yet</p>
                        <p className="text-sm">Add training records, certificates, or sign-offs</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-4">
                    {!isViewer && (
                      <div className="flex gap-2">
                        <Input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
                        <Button onClick={handleAddComment} disabled={!newComment.trim()}><Send className="h-4 w-4" /></Button>
                      </div>
                    )}

                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {selectedItem.activity?.slice().reverse().map(activity => (
                          <div key={activity.id} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{activity.user_name}</span>
                              <span className="text-xs text-muted-foreground">{formatTimestamp(activity.timestamp)}</span>
                            </div>
                            <p className="text-sm">
                              {activity.action === 'comment' ? activity.details : activity.action === 'status_changed' ? (
                                <span>Changed status from <Badge variant="outline">{activity.old_value}</Badge> to <Badge variant="outline">{activity.new_value}</Badge></span>
                              ) : (activity.details || activity.action.replace(/_/g, ' '))}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>

              {!isViewer && (
                <DialogFooter>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedItem.id)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-3xl p-0">
          {selectedDoc && (
            <div className="relative">
              <img src={selectedDoc.url} alt={selectedDoc.name} className="w-full max-h-[80vh] object-contain" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-medium">{selectedDoc.name}</p>
                <p className="text-white/70 text-sm">Uploaded by {selectedDoc.uploaded_by_name} • {formatTimestamp(selectedDoc.uploaded_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CompetencyPanel;
